import axios, { AxiosResponse, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { AppConfig } from './../../../Config';
import { showErrorSnackbar } from '../Utils/SnackbarService';

export interface ReaderlyRequestConfig extends AxiosRequestConfig {
  suppressGlobalError?: boolean;
}

export class ApiError extends Error {
  status: number;
  payload: unknown;

  constructor(status: number, payload: unknown, fallbackMessage = 'Запит не виконано') {
    const message = typeof payload === 'string' && payload.trim() ? payload : fallbackMessage;
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

const httpClient = axios.create({
  baseURL: AppConfig.ReaderlyApiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  validateStatus: () => true,
});

httpClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

httpClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const suppressGlobalError = (response.config as ReaderlyRequestConfig).suppressGlobalError;

    if (response.status === 401) {
      localStorage.removeItem('token');
      window.dispatchEvent(new Event('readerly:auth-expired'));
      if (!suppressGlobalError) showErrorSnackbar('Сесія закінчилась. Увійдіть ще раз.');
      return response;
    }

    if (!(response.status >= 200 && response.status < 300) && !suppressGlobalError) {
      showErrorSnackbar('Не вдалося виконати запит');
    }
    return response;
  },
  (error) => {
    if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    const suppressGlobalError = (error?.config as ReaderlyRequestConfig | undefined)?.suppressGlobalError;

    if (error?.response?.status === 401) {
      localStorage.removeItem('token');
      window.dispatchEvent(new Event('readerly:auth-expired'));
      if (!suppressGlobalError) showErrorSnackbar('Сесія закінчилась. Увійдіть ще раз.');
    } else if (!suppressGlobalError) {
      showErrorSnackbar("Не вдалося з'єднатися з сервером");
    }
    return Promise.reject(error);
  }
);

const readResponse = <T>(response: AxiosResponse<T>): T => {
  if (response.status >= 200 && response.status < 300) {
    return response.data;
  }

  throw new ApiError(response.status, response.data);
};

export const get = async <T>(
  url: string,
  queryParams?: Record<string, any>,
  config?: ReaderlyRequestConfig
): Promise<T> => {
  const finalConfig: ReaderlyRequestConfig = {
    ...config,
    params: { ...config?.params, ...queryParams },
  };
  const response: AxiosResponse<T> = await httpClient.get(url, finalConfig);
  return readResponse(response);
};

export const post = async <T, U>(
  url: string,
  data: U,
  queryParams?: Record<string, any>,
  config?: ReaderlyRequestConfig
): Promise<T> => {
  const finalConfig: ReaderlyRequestConfig = {
    ...config,
    params: { ...config?.params, ...queryParams },
  };
  const response: AxiosResponse<T> = await httpClient.post(url, data, finalConfig);
  return readResponse(response);
};

export const put = async <T, U>(
  url: string,
  data: U,
  queryParams?: Record<string, any>,
  config?: ReaderlyRequestConfig
): Promise<T> => {
  const finalConfig: ReaderlyRequestConfig = {
    ...config,
    params: { ...config?.params, ...queryParams },
  };
  const response: AxiosResponse<T> = await httpClient.put(url, data, finalConfig);
  return readResponse(response);
};

export const patch = async <T, U>(
  url: string,
  data: U,
  queryParams?: Record<string, any>,
  config?: ReaderlyRequestConfig
): Promise<T> => {
  const finalConfig: ReaderlyRequestConfig = {
    ...config,
    params: { ...config?.params, ...queryParams },
  };
  const response: AxiosResponse<T> = await httpClient.patch(url, data, finalConfig);
  return readResponse(response);
};

export const del = async <T>(
  url: string,
  queryParams?: Record<string, any>,
  config?: ReaderlyRequestConfig
): Promise<T> => {
  const finalConfig: ReaderlyRequestConfig = {
    ...config,
    params: { ...config?.params, ...queryParams },
  };
  const response: AxiosResponse<T> = await httpClient.delete(url, finalConfig);
  return readResponse(response);
};

export default httpClient;
